import { z } from '@/libs';
import { addressSchema } from '@/types/models/Address';
import {
  addFullnameValidationIssue,
  addSocialNameValidationIssues,
  customerRegistrationBaseSchema,
} from '@/types/models/CustomerRegistrationForm';
import {
  normalizeNameChars,
  isValidCEP,
  isValidEmail,
  isValidPhone,
  parseDigits,
  validateCPF,
} from '@/utils';
import { removeDDIFromAutoInsert } from '@/utils/formatters';

export interface CustomerProfileAddress {
  cep: string;
  state: string;
  city: string;
  district: string;
  street: string;
  number: string;
  complement?: string;
}

export interface CustomerProfile {
  name?: string;
  socialName?: string | null;
  email?: string;
  phone?: string;
  marketingOptIn?: boolean;
  address?: CustomerProfileAddress;
}

export const customerProfileFormSchema = customerRegistrationBaseSchema
  .extend({
    customerId: z.string().trim().min(1),
    document: z.string().trim().min(1, 'CPF é obrigatório'),
    phone: z.string().trim().min(1, 'Telefone é obrigatório'),
    phoneConfirmation: z.string().trim().min(1, 'Confirmação de telefone é obrigatória'),
    email: z.string().trim().email('E-mail inválido'),
    confirmEmail: z.string().trim().email('E-mail inválido'),
    marketingOptIn: z.boolean().optional(),
    ...addressSchema.shape,
  })
  .transform((data) => ({
    ...data,
    document: parseDigits(data.document),
    phone: removeDDIFromAutoInsert(parseDigits(data.phone)),
    phoneConfirmation: removeDDIFromAutoInsert(parseDigits(data.phoneConfirmation)),
    email: data.email.trim().toLowerCase(),
    confirmEmail: data.confirmEmail.trim().toLowerCase(),
    socialName: data.socialName ? normalizeNameChars(data.socialName) : undefined,
    complement: data.complement?.trim() || '',
    zipCode: parseDigits(data.zipCode),
  }))
  .superRefine((data, ctx) => {
    const documentError = validateCPF(data.document);
    if (documentError) {
      ctx.addIssue({
        path: ['document'],
        code: z.ZodIssueCode.custom,
        message: documentError,
      });
    }

    const phoneError = isValidPhone(data.phone);
    if (phoneError) {
      ctx.addIssue({
        path: ['phone'],
        code: z.ZodIssueCode.custom,
        message: phoneError,
      });
    }

    const confirmPhoneError = isValidPhone(data.phoneConfirmation);
    if (confirmPhoneError) {
      ctx.addIssue({
        path: ['phoneConfirmation'],
        code: z.ZodIssueCode.custom,
        message: confirmPhoneError,
      });
    }

    if (data.phone !== data.phoneConfirmation) {
      ctx.addIssue({
        path: ['phoneConfirmation'],
        code: z.ZodIssueCode.custom,
        message: 'Os telefones não coincidem',
      });
    }

    if (!isValidEmail(data.email)) {
      ctx.addIssue({
        path: ['email'],
        code: z.ZodIssueCode.custom,
        message: 'E-mail inválido',
      });
    }

    if (!isValidEmail(data.confirmEmail)) {
      ctx.addIssue({
        path: ['confirmEmail'],
        code: z.ZodIssueCode.custom,
        message: 'E-mail inválido',
      });
    }

    if (data.email !== data.confirmEmail) {
      ctx.addIssue({
        path: ['email'],
        code: z.ZodIssueCode.custom,
        message: 'Os e-mails não coincidem',
      });
      ctx.addIssue({
        path: ['confirmEmail'],
        code: z.ZodIssueCode.custom,
        message: 'Os e-mails não coincidem',
      });
    }

    if (!isValidCEP(data.zipCode)) {
      ctx.addIssue({
        path: ['zipCode'],
        code: z.ZodIssueCode.custom,
        message: 'CEP inválido',
      });
    }

    addFullnameValidationIssue(data.fullname, ctx);

    if (data.hasSocialName) {
      addSocialNameValidationIssues(data.socialName, ctx);
    }
  });

export type CustomerProfileFormInput = z.input<typeof customerProfileFormSchema>;
export type CustomerProfileFormData = z.output<typeof customerProfileFormSchema>;
